/* global RTCIceCandidate */
import { Record, List, Map } from 'immutable';
import WebRTCApi, { TrackTransceivers, IEnabledChannels } from 'app/services/rtc/WebRTCApi';
import ConnectionMediaDump from 'app/services/rtc/ConnectionMediaDump';
import { bindDomEvents, unbindDomEvents } from 'app/utils/DomEvents';
import { IIceCandidateMessage, ITrackMids } from '@engoo/realtime-protocol/signaling/negotiation';
import ConnectionState from 'app/services/rtc/ConnectionState';
import { EventHandlerMap } from 'app/utils/EventMapEmitter';
import Streams, { getFallbackMid, IStreams } from 'app/services/rtc/Streams';
import { IStatsSender } from 'app/services/rtc/rtcstats';

export const enum RenegotiationReason {
  /** Local `RTCPeerConnection.onnegotiationneeded` event */
  LocalEvent = 'localNegotiationNeeded',
  /** Remote `RTCPeerConnection.onnegotiationneeded` event */
  RemoteEvent = 'remoteNegotiationNeeded',
  /** Creating or parsing local offer failed */
  LocalOfferFail = 'localOfferFail',
  /** Remote answerer failed to parse our offer or to create their answer */
  RemoteAnswerFail = 'remoteAnswerFail',
  /** A connection attempt took too long */
  Timeout = 'timeout',
  /** Any other reason */
  Fail = 'fail',
}

interface IRenegotiation {
  /**
   * Tells whether we need to recreate the connection object
   */
  reset: boolean;
  /**
   * Purely for debugging purposes
   */
  reasons: List<RenegotiationReason>;
}

export interface IRenegotiationInit {
  reset: boolean;
  reason: RenegotiationReason;
}

// Represents an intent for renegotiation, renegotiation is not meant
// to trigger immediately, rather multiple intents in short window of
// time should be merged into one intent. `reset` flag represents
// severity of the renegotiation and can't be changed to false until
// renegotiation is complete.
class Renegotiation extends Record<IRenegotiation>({
  reset:   false,
  reasons: List(),
}, 'Renegotiation') {
  public constructor({ reason, reset }: IRenegotiationInit) {
    super({
      ...reset ? { reset } : null,
      ...reason ? { reasons: List([reason]) } : null,
    });
  }

  public mergeIntent({ reason, reset }: IRenegotiationInit) {
    return this.merge({
      reset: this.reset || reset,
      reasons: this.reasons.push(reason),
    });
  }
}

export class Mids extends Record<ITrackMids>({
  audio: undefined,
  video: undefined,
  screenAudio: undefined,
  screenVideo: undefined,
}) {
  public static fromTransceivers(transceivers: TrackTransceivers) {
    return new Mids({
      audio: transceivers.audio?.mid,
      video: transceivers.video?.mid,
      screenAudio: transceivers.screenAudio?.mid,
      screenVideo: transceivers.screenVideo?.mid,
    });
  }
}

interface INegotiationState {
  config: RTCConfiguration;
  channels: IEnabledChannels;
  eventMap: EventHandlerMap<RTCPeerConnectionEventMap>;
  tracer?: (connection: RTCPeerConnection) => IStatsSender;
  statsSender?: IStatsSender;
  renegotiation: Renegotiation | null;
  connection: RTCPeerConnection | null;
  connectionState: ConnectionState;
  error: unknown;
  offer: string;
  answer: string;
  transceivers: TrackTransceivers,
  localMids: Mids;
  localStreams: Streams;
  // These three are a snapshot of fields on the connection object,
  // this way we can observe changes to them
  signalingState: RTCSignalingState | null;
  iceGatheringState: RTCIceGatheringState | null;
  iceConnectionState: RTCIceConnectionState | null;
  remoteMids: Mids;
  /**
   * Map of mid to track
   */
  remoteTracks: Map<string, MediaStreamTrack>;
  /**
   * Lazily-generated collection of streams, from remoteMids and
   * remoteTracks
   */
  remoteStreams: Streams;
  // This is purely for debugging
  media: ConnectionMediaDump;
}

export default class NegotiationState extends Record<INegotiationState>({
  config:             {},
  channels:           { audio: false, video: false },
  eventMap:           {},
  tracer:             undefined,
  statsSender:        undefined,
  renegotiation:      null,
  connection:         null,
  connectionState:    ConnectionState.Off,
  error:              null,
  offer:              '',
  answer:             '',
  transceivers:       {},
  localMids:          new Mids(),
  localStreams:       new Streams(),
  signalingState:     null,
  iceGatheringState:  null,
  iceConnectionState: null,
  media:              new ConnectionMediaDump(),
  remoteMids:         new Mids(),
  remoteTracks:       Map(),
  remoteStreams:      new Streams(),
}, 'NegotiationState') {
  public async start() {
    const { config, eventMap, tracer } = this;
    const connection = WebRTCApi.createPeerConnection(config);
    const statsSender = tracer?.(connection);

    statsSender?.sendPeerMetadata();

    const result = this.merge({
      connection,
      offer:  '',
      answer: '',
      error:  null,
    }).updateStateIndicators();

    // Register `track` with `ontrack` property instead of
    // `addEventListener` because Safari 12.0 and under only support
    // it this way
    const { track: ontrack, ...eventMapRest } = eventMap;
    bindDomEvents(connection, eventMapRest);
    connection.ontrack = ontrack ?? null;

    // Avoid putting statsSender in `merge` because it's going to
    // be converted to immutable Map
    return result.set('statsSender', statsSender);
  }

  public stop() {
    const { connection, eventMap } = this;

    if (connection) {
      unbindDomEvents(connection, eventMap);
      connection.ontrack = null;

      try {
        connection.close();
      } catch (error) {
        // ignore if we try to close a failed connection
      }
    }

    return this.merge({
      connection: null,
      transceivers: {},
      localMids: new Mids({}),
      remoteMids: new Mids({}),
      remoteTracks: Map(),
      remoteStreams: new Streams(),
    }).updateStateIndicators();
  }

  public close() {
    return this
      .stop()
      .merge({
        renegotiation: null,
      })
      .updateStateIndicators();
  }

  public async reboot() {
    return await (this.stop()).start();
  }

  public setLocalAudioStream(stream?: MediaStream) {
    return this.setLocalStream('audio', stream);
  }

  public setLocalVideoStream(stream?: MediaStream) {
    return this.setLocalStream('video', stream);
  }

  public setLocalScreenStream(stream?: MediaStream) {
    return this.setLocalStream('screen', stream);
  }

  /**
   * Set media stream for particular mid.
   *
   * Also, sets the same stream for fallback mids. That's needed
   * if remoteMids are not provided by the sending side.
   */
  public setRemoteTrack(mid: string, track: MediaStreamTrack) {
    return this.update('remoteTracks', (tracks) => tracks
      .set(mid, track)
      .set(getFallbackMid(track), track),
    );
  }

  /**
   * Set media stream without knowing mids
   *
   * In case mids are unknown, guess tracks's role based on their `kind`
   *
   * This also sets `remoteStreams` to make `Streams.mergeTracks`
   * a no-op because Edge doesn't like to move tracks between streams
   * (will see no data coming through).
   */
  public setRemoteStreamFallback(stream: MediaStream): this {
    return stream.getTracks().reduce(
      (acc, track) => acc.update(
        'remoteTracks',
        (tracks) => tracks.set(getFallbackMid(track), track),
      ),
      this,
    ).update(
      'remoteStreams',
      (streams) => streams.mergeStream(stream),
    );
  }

  public setRenegotiationIntent({
    reset = false,
    reason = RenegotiationReason.LocalEvent,
  }: Partial<IRenegotiationInit> = {}) {
    if (this.renegotiation) {
      const updatedRenegotiation = this.renegotiation.mergeIntent({ reset, reason });
      return this.set('renegotiation', updatedRenegotiation);
    } else {
      return this.set('renegotiation', new Renegotiation({ reset, reason }));
    }
  }

  public setError(error: unknown) {
    return this.set('error', error);
  }

  public doneRenegotiation() {
    return this.set('renegotiation', null);
  }

  // We shouldn't try to accept or send out any ICE candidates if SDP
  // exchange failed
  public readyForICE() {
    return !!this.connection && !!this.offer && !!this.answer;
  }

  public updateStateIndicators() {
    const {
      signalingState,
      iceGatheringState,
      iceConnectionState,
    } = this.connection || {
      signalingState:     'closed' as const,
      iceGatheringState:  'complete' as const,
      iceConnectionState: 'closed' as const,
    };

    const connectionState =
      signalingState === 'closed' ? ConnectionState.Off :
      IceConnectionStateMap[iceConnectionState];

    return this.merge({
      connectionState,
      signalingState,
      iceConnectionState,
      iceGatheringState,
      media: new ConnectionMediaDump(this.connection),
      localMids: Mids.fromTransceivers(this.transceivers),
      remoteStreams: this.remoteStreams.mergeTracks(this.remoteMids, this.remoteTracks),
    });
  }

  public async addIceCandidate(candidate: IIceCandidateMessage['candidate']) {
    const { connection } = this;

    if (!(this.readyForICE() && connection)) {
      return this;
    }

    // `candidate` field can be an empty string as in the spec for
    // `addIceCandidate` to notify an end of list of candidates [1].
    // Safari though breaks on this. [2]
    //
    // [1]: https://w3c.github.io/webrtc-pc/#dom-peerconnection-addicecandidate
    // [2]: https://bugs.webkit.org/show_bug.cgi?id=199316
    try {
      await connection.addIceCandidate(
        candidate === null
        ? null
        : new RTCIceCandidate(candidate),
      );
    } catch (error) {
      // If `candidate` is null or `candidate.candidate` is an empty
      // string, ignore the error because there's a varying level
      // of support for the `a=end-of-candidates` SDP across browses
      if (candidate && candidate.candidate) {
        throw error;
      }
    }

    return this;
  }

  /**
   * Private low level method to set streams
   */
  private async setLocalStream(
    name: keyof IStreams,
    stream?: MediaStream,
  ) {
    let next = this.update('localStreams', (streams) => streams.set(name, stream));

    if (!stream) {
      return next;
    }

    if (!next.connection) {
      next = next.setRenegotiationIntent({ reason: RenegotiationReason.LocalEvent });
    }

    return next.updateStateIndicators();
  }
}

const IceConnectionStateMap: { [key: string]: ConnectionState } = Object.freeze({
  new:          ConnectionState.Connecting,
  checking:     ConnectionState.Connecting,
  // "disconnected" is a temporary state when there are network
  // problems and can go back to "connected"
  disconnected: ConnectionState.Connecting,
  connected:    ConnectionState.On,
  completed:    ConnectionState.On,
  failed:       ConnectionState.Failed,
  closed:       ConnectionState.Off,
});
